/**
 * The questionnaire.
 *
 * Asked once, at the end, after the product has either proved itself or failed to. Two things
 * are being collected and they are different in kind:
 *
 *   **verification** — did you actually find each other? The proximity check measures two phones
 *   being close, which is usually the same thing and sometimes very much not: a hedge, a fence,
 *   a floor, a crowd. Until somebody says so, the north-star number is a proxy.
 *
 *   **everything else** — optional, and only shown to people who already answered the first
 *   question. A form that opens with seven questions gets answered by nobody.
 *
 * Nothing submitted here carries a session code, a name or a coordinate; the free-text box is
 * scrubbed of anything coordinate-shaped on the server before it is written. That is what makes
 * asking legitimate.
 */

import {
  FEEDBACK_PLACES,
  FEEDBACK_PROBLEMS,
  MAX_COMMENT_LENGTH,
  type FeedbackPlace,
  type FeedbackProblem,
  type FeedbackSubmission,
  type FoundOutcome,
} from '@beeline/shared';

import { el } from '../lib/dom';
import { navigate } from '../lib/navigate';
import type { View } from '../lib/router';
import { markLogo } from '../ui/mark';

/** What the caller knows about the session that just ended. Counts, never identities. */
export interface FeedbackContext {
  groupSize?: number | null;
  usedRallyPoint?: boolean;
}

export interface FeedbackFormOptions extends FeedbackContext {
  /** Fired once the answer is away, or once the user declines to give one. */
  onDone: () => void;
}

/** Post it. Failure is silent by design: a lost opinion must never become an error screen. */
export async function submitFeedback(submission: FeedbackSubmission): Promise<boolean> {
  try {
    const response = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(submission),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * The form, as an element the caller mounts wherever it belongs.
 *
 * Built in two stages: the one question, and then — only if it was answered — the rest.
 */
export function feedbackForm(options: FeedbackFormOptions): HTMLElement {
  const problems = new Set<FeedbackProblem>();
  let place: FeedbackPlace | null = null;
  let rating: number | null = null;

  const root = el('section', { class: 'feedback' });

  const send = (found: FoundOutcome, comment: string | null): void => {
    void submitFeedback({
      found,
      rating,
      problems: [...problems],
      place,
      comment,
      groupSize: options.groupSize ?? null,
      usedRallyPoint: options.usedRallyPoint === true,
    });
    options.onDone();
  };

  /* --- stage two: the optional detail ----------------------------------- */

  const detail = (found: FoundOutcome): HTMLElement => {
    const comment = el('textarea', {
      class: 'field__input feedback__comment',
      rows: '3',
      maxlength: String(MAX_COMMENT_LENGTH),
      placeholder:
        found === 'found'
          ? 'Anything that would have made it easier?'
          : 'What happened? This is the most useful box on the page.',
      'aria-label': 'Anything else',
    }) as HTMLTextAreaElement;

    const chip = (label: string, on: () => boolean, toggle: () => void): HTMLElement => {
      const button = el('button', { class: 'chip chip--action', type: 'button' }, label);
      const paint = (): void => {
        button.classList.toggle('chip--on', on());
        button.setAttribute('aria-pressed', String(on()));
      };
      button.addEventListener('click', () => {
        toggle();
        paint();
      });
      paint();
      return button;
    };

    const problemChips = el(
      'div',
      { class: 'chips' },
      ...FEEDBACK_PROBLEMS.map((problem) =>
        chip(
          problem.label,
          () => problems.has(problem.id),
          () => {
            if (problems.has(problem.id)) problems.delete(problem.id);
            else problems.add(problem.id);
          },
        ),
      ),
    );

    const placeChips = el(
      'div',
      { class: 'chips' },
      ...FEEDBACK_PLACES.map((option) =>
        chip(
          option.label,
          () => place === option.id,
          () => {
            place = place === option.id ? null : option.id;
            // Single-select, so repaint the siblings too.
            for (const sibling of placeChips.querySelectorAll('button')) {
              sibling.classList.toggle(
                'chip--on',
                sibling.textContent === FEEDBACK_PLACES.find((entry) => entry.id === place)?.label,
              );
            }
          },
        ),
      ),
    );

    const ratingRow = el(
      'div',
      { class: 'choices' },
      ...[1, 2, 3, 4, 5].map((value) => {
        const button = el(
          'button',
          { class: 'choice', type: 'button', 'aria-label': `${value} out of 5` },
          String(value),
        );
        button.addEventListener('click', () => {
          rating = value;
          for (const sibling of ratingRow.querySelectorAll('button')) {
            sibling.classList.toggle('choice--on', sibling.textContent === String(rating));
          }
        });
        return button;
      }),
    );

    return el(
      'div',
      { class: 'feedback__detail' },
      el('p', {
        class: 'screen__body',
        text:
          found === 'found'
            ? 'Thanks. If you have ten more seconds, this is what tunes the thresholds.'
            : 'Sorry. This is exactly the case we most need to hear about.',
      }),

      el(
        'div',
        { class: 'field' },
        el('span', { class: 'field__label', text: 'How well did it work?' }),
        ratingRow,
      ),

      el(
        'div',
        { class: 'field' },
        el('span', { class: 'field__label', text: 'Anything go wrong?' }),
        problemChips,
      ),

      el(
        'div',
        { class: 'field' },
        el('span', { class: 'field__label', text: 'Where were you?' }),
        placeChips,
      ),

      el('div', { class: 'field' }, comment),

      el(
        'div',
        { class: 'screen__actions' },
        el(
          'button',
          {
            class: 'button button--primary button--block',
            type: 'button',
            'data-action': 'send-feedback',
            onclick: () => send(found, comment.value.trim() || null),
          },
          'Send it',
        ),
        el(
          'button',
          {
            class: 'button button--ghost button--block',
            type: 'button',
            onclick: () => send(found, null),
          },
          'That is enough',
        ),
      ),

      el('p', {
        class: 'privacy',
        text: 'This is not attached to your session, your name or anywhere you were. It is an opinion about the app and nothing else.',
      }),
    );
  };

  /* --- stage one: the one question -------------------------------------- */

  const answer = (found: FoundOutcome): void => {
    if (found === 'skipped') {
      send('skipped', null);
      return;
    }
    root.replaceChildren(detail(found));
    root.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  root.replaceChildren(
    el('h2', { class: 'feedback__question', text: 'Did you find each other?' }),
    el(
      'div',
      { class: 'feedback__answers' },
      el(
        'button',
        {
          class: 'button button--primary',
          type: 'button',
          'data-action': 'found-yes',
          onclick: () => answer('found'),
        },
        'Yes',
      ),
      el(
        'button',
        {
          class: 'button button--ghost',
          type: 'button',
          'data-action': 'found-no',
          onclick: () => answer('not_found'),
        },
        'No',
      ),
    ),
    el(
      'button',
      {
        class: 'roles__skip',
        type: 'button',
        onclick: () => answer('skipped'),
      },
      'Not now',
    ),
  );

  return root;
}

/**
 * `/feedback` — the same form, reachable on its own.
 *
 * For the person who closed the tab before answering, or who has something to say a week later.
 * It knows nothing about any session, which is the point.
 */
export const feedbackView: View = (container) => {
  const done = (): void => {
    container.replaceChildren(
      el(
        'main',
        { class: 'screen screen--centered' },
        el('div', { class: 'converged__mark', 'aria-hidden': 'true' }),
        el('h1', { class: 'screen__title', text: 'Thank you' }),
        el('p', {
          class: 'screen__body',
          text: 'That genuinely changes what gets built next.',
        }),
        el(
          'button',
          {
            class: 'button button--primary button--block',
            type: 'button',
            onclick: () => navigate('/'),
          },
          'Done',
        ),
      ),
    );
  };

  container.replaceChildren(
    el(
      'main',
      { class: 'screen screen--centered' },
      markLogo(52),
      el('h1', { class: 'screen__title', text: 'How did it go?' }),
      feedbackForm({ onDone: done }),
    ),
  );
};
